import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import './filters.css';

/**
 * Spread on every filter input. They are search fields, not form data, and a
 * label such as "Email contains" otherwise draws password-manager suggestions:
 * autoComplete="off" covers the browser, the data attributes are the opt-outs
 * 1Password, LastPass, Bitwarden and Dashlane honour (they ignore autocomplete).
 */
export const NO_AUTOFILL = {
  autoComplete: 'off',
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-bwignore': 'true',
  'data-form-type': 'other',
} as const;

export type Align = 'left' | 'right';
export type SortDir = 'asc' | 'desc';

/** Sort props shared by every column header popover. */
export interface SortProps {
  sortable?: boolean;
  sortActive?: boolean;
  sortDir?: SortDir;
  onSort?: (dir: SortDir) => void;
}

interface RenderArgs<T> {
  draft: T;
  setDraft: (next: T) => void;
}

interface FilterPopoverProps<T> extends SortProps {
  label: string;
  /** Small, non-uppercased qualifier shown after the label (e.g. a time zone). */
  hint?: string;
  align?: Align;
  filterActive?: boolean;
  value: T;
  emptyValue: T;
  onChange: (next: T) => void;
  render: (args: RenderArgs<T>) => ReactNode;
  width?: string;
  isEmpty?: (draft: T) => boolean;
}

/**
 * Clickable column header, ported from the nge-* dashboards
 * (nge-fuel dashboard-ui/src/components/filters/FilterPopover.tsx): the label
 * opens a popover holding the sort actions and the column's filter, edited as a
 * draft and committed with Apply (Enter) or discarded with Cancel. An active
 * sort shows an arrow, an active filter a dot. Clicking outside or pressing
 * Escape closes it without applying.
 */
export function FilterPopover<T>({
  label,
  hint,
  align = 'left',
  sortable = false,
  sortActive = false,
  sortDir = 'asc',
  onSort,
  filterActive = false,
  value,
  emptyValue,
  onChange,
  render,
  width = '16rem',
  isEmpty,
}: FilterPopoverProps<T>) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<T>(value);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const handleEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const toggle = () => {
    // Every opening starts from the applied value, dropping any cancelled draft
    if (!open) setDraft(value);
    setOpen(!open);
  };

  const close = () => setOpen(false);

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  const clear = () => {
    setDraft(emptyValue);
    onChange(emptyValue);
    setOpen(false);
  };

  const sort = (dir: SortDir) => {
    onSort?.(dir);
    setOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Enter applies from any field; on a button it keeps activating that button
    if (e.key === 'Enter' && !(e.target instanceof HTMLButtonElement)) {
      e.preventDefault();
      apply();
    }
  };

  const draftIsEmpty = isEmpty ? isEmpty(draft) : false;

  return (
    <div className="column-filter" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`column-filter-trigger ${sortActive || filterActive ? 'active' : ''} ${open ? 'open' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        <span>{label}</span>
        {hint && <span className="column-filter-hint">{hint}</span>}
        {sortActive && <span className="column-filter-sort-indicator">{sortDir === 'asc' ? '↑' : '↓'}</span>}
        {filterActive && <span className="column-filter-dot" aria-label="Filter active" />}
        <span className="column-filter-chevron" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div
          className={`column-filter-popover align-${align}`}
          style={{ width }}
          role="dialog"
          aria-label={`Sort and filter ${label}`}
          onKeyDown={handleKeyDown}
        >
          {sortable && onSort && (
            <>
              <div className="column-filter-sort">
                <SortButton icon="↑" label="Sort ascending" active={sortActive && sortDir === 'asc'} onClick={() => sort('asc')} />
                <SortButton icon="↓" label="Sort descending" active={sortActive && sortDir === 'desc'} onClick={() => sort('desc')} />
              </div>
              <div className="column-filter-divider" />
            </>
          )}
          <div className="column-filter-section">
            <div className="column-filter-section-header">
              <span className="column-filter-section-title">Filter</span>
              {filterActive && (
                <button type="button" className="column-filter-clear" onClick={clear}>
                  × Clear
                </button>
              )}
            </div>
            {render({ draft, setDraft })}
            <div className="column-filter-actions">
              <button type="button" className="column-filter-cancel" onClick={close}>
                Cancel
              </button>
              <button
                type="button"
                className="column-filter-apply"
                onClick={apply}
                disabled={draftIsEmpty && !filterActive}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SortButton({ icon, label, active, onClick }: { icon: string; label: string; active: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`column-filter-sort-button ${active ? 'active' : ''}`} onClick={onClick}>
      <span aria-hidden="true">{icon}</span>
      {label}
    </button>
  );
}
